import { FileText, Users, CheckCircle } from 'lucide-react';
import { useState } from 'react';
import DiagnosticModal from './DiagnosticModal';
import bgPattern from '../assets/bg-aurorerh_(1).svg';

interface Resource {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  url: string;
  color: string;
}

export default function FreeResources() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);

  const handleResourceClick = (resource: Resource) => {
    if (window.innerWidth >= 1024) {
      setSelectedResource(resource);
      setModalOpen(true);
    } else {
      window.open(resource.url, '_blank', 'noopener,noreferrer');
    }
  };
  const resources: Resource[] = [
    {
      id: 'diagnostic',
      title: 'Mini diagnostic RH, obligation légale pour les dirigeants',
      description: 'Évaluez rapidement vos obligations légales en tant que dirigeant et assurez-vous d\'être en conformité.',
      icon: <FileText className="w-8 h-8" />,
      url: 'https://tally.so/r/lbbMDW',
      color: 'from-[#A0523F] to-aurore-dark'
    },
    {
      id: 'management',
      title: 'Découvre ton style managérial',
      description: 'Identifiez votre style de management et découvrez comment l\'optimiser pour une meilleure efficacité d\'équipe.',
      icon: <Users className="w-8 h-8" />,
      url: 'https://tally.so/r/2EjaXD',
      color: 'from-aurore-dark to-aurore-medium'
    },
    {
      id: 'compliance',
      title: 'Ton activité freelance est-elle conforme',
      description: 'Vérifiez que votre activité de freelance respecte toutes les obligations légales et administratives.',
      icon: <CheckCircle className="w-8 h-8" />,
      url: 'https://tally.so/r/XxxNdP',
      color: 'from-aurore-medium to-aurore-light'
    }
  ];

  return (
    <section
      className="py-24 bg-gradient-to-b from-aurore-pale to-warm-gray-50 dark:from-warm-gray-900 dark:to-warm-gray-800 bg-cover bg-center bg-no-repeat relative"
      style={{ backgroundImage: `url(${bgPattern})` }}
    >
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-5 py-2.5 bg-aurore-light/50 dark:bg-warm-gray-700 rounded-full">
              <span className="text-aurore-dark dark:text-aurore-light text-sm font-medium">Outils gratuits</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-8 leading-tight">
              Faites le point sur votre situation en 5 minutes
            </h2>
            <p className="text-xl text-warm-gray-700 dark:text-warm-gray-300 max-w-3xl mx-auto leading-relaxed-plus">
              Diagnostic RH, test de conformité, style managérial… Ces quiz gratuits vous donnent déjà des pistes concrètes.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {resources.map((resource) => (
              <div
                key={resource.id}
                className="group bg-white dark:bg-warm-gray-800 rounded-3xl shadow-sm hover:shadow-lg transition-all duration-500 overflow-hidden border border-aurore-light/20 dark:border-warm-gray-700 flex flex-col"
              >
                <div className={`bg-gradient-to-r ${resource.color} p-10 text-white h-48 flex flex-col justify-center`}>
                  <div className="flex justify-center mb-5">
                    {resource.icon}
                  </div>
                  <h3 className="text-xl font-bold text-center leading-tight">
                    {resource.title}
                  </h3>
                </div>

                <div className="p-8 flex flex-col flex-grow">
                  <p className="text-warm-gray-700 dark:text-warm-gray-300 mb-8 text-center leading-relaxed-plus flex-grow">
                    {resource.description}
                  </p>

                  <button
                    onClick={() => handleResourceClick(resource)}
                    className="block w-full text-center px-8 py-4 bg-aurore-dark text-white rounded-2xl hover:bg-aurore-medium hover:shadow-md transition-all duration-500 font-medium"
                  >
                    Commencer
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-warm-gray-600 dark:text-warm-gray-400 text-base leading-relaxed">
              100% gratuit. Aucune inscription. Vos réponses restent confidentielles.
            </p>
          </div>
        </div>
      </div>

      <DiagnosticModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        url={selectedResource?.url || ''}
        title={selectedResource?.title || ''}
      />
    </section>
  );
}
